import styled from "@emotion/styled";
import type { NormalizedTokenAccount } from "@stepfinance/tokens";
import { Collapse } from "antd";
import type { FC } from "react";
import React, { useMemo } from "react";

import { useLanguageContext } from "../../contexts/language/language";
import { useLiquidity, useOwnedPools } from "../../contexts/liquidity";
import { getExpandIcon } from "../shared";
import type { AddToLiquidityProps } from "./AddToLiquidity";
import { PoolLabelHeader } from "./PoolLabelHeader";
import { YourPoolsPanel } from "./YourPoolsPanel";

export type YourPoolsProps = {
  onPoolLiquidityAction?: AddToLiquidityProps["onPoolLiquidityAction"];
};

export const YourPools: FC<YourPoolsProps> = ({ onPoolLiquidityAction }) => {
  const { stepFinancePools } = useLiquidity();
  const pools = useOwnedPools();

  const {
    currentLanguageData: {
      pages: {
        swapLP: {
          liquidityTab: { yourPools: text },
        },
      },
    },
  } = useLanguageContext();

  const uniquePoolSet = useMemo(() => {
    const poolSet = new Set<string>();
    pools.forEach(function (item) {
      const poolPositionGreaterThanZero = item.account.tokenAmount.uiAmount > 0;
      const isStepFinancePool = item.pool.kind === "Step Finance";
      const poolSetAlreadyHasMint = poolSet.has(item.pool.mintAddress);
      if (poolPositionGreaterThanZero && isStepFinancePool && !poolSetAlreadyHasMint) {
        poolSet.add(item.pool.mintAddress);
      }
    });
    return poolSet;
  }, [pools]);

  if (uniquePoolSet.size === 0) {
    return null;
  }

  const accountPoolMap = pools.reduce(function (map, obj) {
    const key = obj.pool.mintAddress.toString();
    const accounts = map.has(key) ? map.get(key)! : [];
    accounts.push(obj.account);
    map.set(key, accounts);
    return map;
  }, new Map<string, NormalizedTokenAccount[]>());

  return (
    <YourPoolsContainer>
      <YourPoolsInfoHeaderContainer>
        <YourPoolsSpan>{text}</YourPoolsSpan>
        <PoolLabelHeader />
      </YourPoolsInfoHeaderContainer>
      <YourPoolsCollapse expandIcon={getExpandIcon} expandIconPosition="end" bordered={false}>
        {Array.from(uniquePoolSet).map((poolMintAddress) => (
          <YourPoolsPanel
            key={poolMintAddress}
            pool={stepFinancePools[poolMintAddress]!}
            accounts={accountPoolMap.get(poolMintAddress) ?? []}
            onPoolLiquidityAction={onPoolLiquidityAction}
          />
        ))}
      </YourPoolsCollapse>
    </YourPoolsContainer>
  );
};

const YourPoolsContainer = styled.div`
  width: 100%;
`;

const YourPoolsInfoHeaderContainer = styled.div`
  text-align: initial;
  margin-top: 30px;
  width: 100%;
  span {
    display: block;
  }
`;

const YourPoolsSpan = styled.span`
  margin-bottom: 20px;
`;

const YourPoolsCollapse = styled(Collapse)`
  background: none;

  .ant-collapse-item {
    overflow: hidden;
    border: none;
    :last-child {
      border-radius: 8px;
    }
  }

  .ant-collapse-item:not(:last-child) {
    margin-bottom: 10px;
  }

  .ant-collapse-content {
    overflow: hidden;
  }
`;
